import { clsx } from 'clsx'

import {
  Chat,
  Message,
  MessageContentType,
} from '@tribeplatform/gql-client/types'
import { PendingMessage } from '@tribeplatform/react-sdk/hooks'
import { Media } from '@tribeplatform/react-ui-kit/Media'

import { ErrorBoundary } from '../../../common/components/ErrorBoundary/ErrorBoundary.js'
import { MediaModalBoundary } from '../../../MediaModal/MediaModalBoundary.js'
import { useChatContext } from '../../ChatContext/ChatContext.js'
import { DraftMessageType } from '../../types.js'
import { isPendingMessage } from '../utils/chat.utils.js'

import { ChatLogMessage } from './ChatLogMessage.js'
import { ChatMessageAuthorName } from './ChatMessageAuthorName.js'
import { ChatMessageAvatar } from './ChatMessageAvatar.js'
import { ChatMessageContextMenu } from './ChatMessageContextMenu.js'
import { MessageActions } from './MessageActions.js'
import { MessageRenderer } from './MessageRenderer.js'
import { NewMessageDivider } from './NewMessageDivider.js'

interface Props {
  id: string
  message: Message | PendingMessage
  isLastMessage: boolean
  isLestReadMessage: boolean
  chat: Chat
  layout?: 'default' | 'compact'
  className?: string
  onSelected?: (message: Message) => void
  onEdit?: (message: Message) => void
}

export const ChatMessage = ({
  id,
  message,
  isLastMessage,
  isLestReadMessage,
  chat,
  layout = 'default',
  className,
  onSelected,
  onEdit,
}: Props) => {
  const { draftMessage } = useChatContext()
  const isEditing = draftMessage.type === DraftMessageType.Edit
  const isBeingEdited =
    isEditing && draftMessage.originalMessage?._id === message._id

  if (message.contentType === MessageContentType.ChatLog) {
    return <ChatLogMessage message={message as Message} />
  }
  const isPending = isPendingMessage(message)
  const isCurrentUserMsg =
    message.authorParticipantId === chat?.currentParticipant?._id
  const showAuthorName = layout !== 'compact'
  const showMessageActions = layout !== 'compact' && !isPending && !isEditing

  return (
    <ErrorBoundary>
      <MediaModalBoundary>
        <ChatMessageContextMenu
          onSelected={() => {
            if (!isPendingMessage(message)) {
              onSelected?.(message)
            }
          }}
        >
          <div
            className={clsx(
              'flex gap-x-2 px-2 sm:px-8 max-w-full group relative transition-opacity duration-200',
              isPending && 'animate-pulse',
              layout === 'compact' && 'gap-x-3',
              isEditing && !isBeingEdited && 'opacity-30',
              className,
            )}
          >
            <Media
              className="flex-col grow-0 justify-start"
              id={id}
              icon={
                <ChatMessageAvatar
                  chat={chat}
                  message={message}
                  isMe={isCurrentUserMsg}
                />
              }
              truncate={false}
            />
            <div className="flex flex-col gap-y-1 w-full rounded-base max-w-full overflow-hidden">
              <div className="flex items-center gap-x-2 justify-between ">
                {showAuthorName && (
                  <ChatMessageAuthorName
                    message={message}
                    isMe={isCurrentUserMsg}
                    chat={chat}
                  />
                )}
                {showMessageActions && (
                  <MessageActions
                    message={message}
                    isCurrentUserMsg={isCurrentUserMsg}
                    onEdit={() => onEdit?.(message)}
                  />
                )}
              </div>
              <div className="flex flex-col rounded-base sm:max-w-prose ">
                <MessageRenderer message={message} layout={layout} />
              </div>
            </div>
          </div>
          {isLestReadMessage && !isLastMessage && <NewMessageDivider />}
        </ChatMessageContextMenu>
      </MediaModalBoundary>
    </ErrorBoundary>
  )
}
